import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Loader2, AlertTriangle, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const OPTIONS = [["ready", "Your study is ready"], ["updated", "Updated version of your study"], ["draft", "Draft for your review"]];
const when = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export const EmailPdf = ({ study, client }) => {
  const [open, setOpen] = useState(false);
  const [tpl, setTpl] = useState(study.paid ? "ready" : "draft");
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!open) return;
    setPreview(null);
    api.post(`/studies/${study.id}/email-pdf/preview`, { template: tpl }).then((r) => setPreview(r.data)).catch((e) => setPreview({ error: errMsg(e, "Preview unavailable") }));
  }, [open, tpl, study.id]);
  useEffect(() => { if (study.client_id) api.get(`/studies/${study.id}/emails`).then((r) => setLog(r.data)).catch(() => {}); }, [study.id, study.client_id]);
  useEffect(() => { setTpl(study.paid ? "ready" : "draft"); }, [study.paid]);
  if (!study.client_id || study.status !== "ready") return null;
  const send = async () => {
    setBusy(true);
    try { const r = await api.post(`/studies/${study.id}/email-pdf`, { template: tpl }); setLog((l) => [r.data, ...l]); setOpen(false); toast.success(`PDF link emailed to ${r.data.to}`); }
    catch (e) { toast.error(errMsg(e, "Could not send email")); } finally { setBusy(false); }
  };
  const opts = study.paid ? OPTIONS.filter(([k]) => k !== "draft") : OPTIONS.filter(([k]) => k === "draft");
  return (
    <div className="no-print rounded-xl border border-slate-800 bg-slate-900/70 px-5 py-4 space-y-3" data-testid="email-pdf">
      <div className="flex flex-wrap items-center gap-3">
        <Mail className="h-4 w-4 text-emerald-400" />
        <span className="text-sm text-slate-300 flex-1">Email PDF to client<span className="block text-xs text-slate-500">Sends a secure 7-day download link{study.paid ? "" : ". This study is unpaid, so the client gets the DRAFT PDF and a pay link"}.</span></span>
        <Button data-testid="email-pdf-btn" size="sm" onClick={() => setOpen(true)} className="rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400"><Mail className="h-4 w-4 mr-1" />Email PDF</Button>
      </div>
      {log.length > 0 && (
        <div className="divide-y divide-slate-800 rounded-lg border border-slate-800" data-testid="email-pdf-log">
          {log.slice(0, 6).map((e) => (
            <div key={e.id} className="flex flex-wrap items-center gap-3 px-3 py-2 text-xs" data-testid="email-pdf-log-row">
              <span className={`uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${e.draft ? "bg-amber-500/15 text-amber-300" : "bg-emerald-500/15 text-emerald-300"}`}>{e.draft ? "Draft" : "Final"}</span>
              <span className="text-slate-300 flex-1">{OPTIONS.find(([k]) => k === e.template)?.[1]} to {e.to} · v{e.version} · by {e.by}</span>
              <span className="flex items-center gap-1 text-slate-500"><Download className="h-3 w-3" />{e.downloads}</span>
              <span className="text-slate-500 num">{when(e.at)}</span>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-5xl bg-slate-900 border-slate-800 text-slate-100" data-testid="email-pdf-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Email PDF to client</DialogTitle>
            <DialogDescription className="text-slate-400">The email has a “Download your study (PDF)” button with a secure link that works for 7 days.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-6 max-h-[75vh] overflow-y-auto lg:overflow-visible">
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-3 text-sm">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">To</p>
              <p data-testid="email-pdf-to">{client?.name} · <span className="text-slate-300">{client?.email || "no email on file"}</span></p>
              <p className="text-xs text-slate-500 mt-1">To send elsewhere, update the client's email in <Link to="/clients" data-testid="email-pdf-clients-link" className="text-emerald-400 hover:underline">Clients</Link>.</p>
            </div>
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wider text-slate-500">Message</p>
              {opts.map(([k, label]) => (
                <label key={k} className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer ${tpl === k ? "border-emerald-500/60 bg-emerald-500/5" : "border-slate-800"}`} data-testid={`email-pdf-template-${k}`}>
                  <input type="radio" name="tpl" checked={tpl === k} onChange={() => setTpl(k)} className="accent-emerald-500" />{label}
                </label>
              ))}
            </div>
            {!study.paid && <p className="flex gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200" data-testid="email-pdf-draft-warning"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />This study isn't paid yet. The client will receive a DRAFT-watermarked PDF and a link to pay the invoice.</p>}
          </div>
          <div className="space-y-2" data-testid="email-pdf-preview">
            <p className="text-xs uppercase tracking-wider text-slate-500">Preview: exactly what the client receives</p>
            {preview?.subject && <p className="text-sm" data-testid="email-pdf-preview-subject"><span className="text-slate-500">Subject: </span>{preview.subject}</p>}
            <div className="h-[420px] overflow-hidden rounded-lg border border-slate-700 bg-slate-100">
              {!preview ? <div className="h-full grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-500" /></div>
                : preview.error ? <p className="p-4 text-sm text-rose-600">{preview.error}</p>
                : <iframe title="Email preview" data-testid="email-pdf-preview-frame" sandbox="" srcDoc={preview.html} className="h-full w-full bg-white" />}
            </div>
            <p className="text-[11px] text-slate-500">The download button gets a unique secure link when you send.</p>
          </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" data-testid="email-pdf-cancel" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="email-pdf-send" disabled={busy || !client?.email} onClick={send} className="rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400">{busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Send email</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
